import { MAX_PUSH_OPERATIONS, type Dataset, type PullResponse, type PushRequest, type PushResponse, type SyncOperation } from "@otrack/domain";
import type { LocalStore } from "./index";

export interface SyncApi {
  push(request: PushRequest): Promise<PushResponse>;
  pull(after: number): Promise<PullResponse>;
}

export class SyncRequestError extends Error {
  constructor(message: string, readonly status: number | null, readonly retryable: boolean) {
    super(message);
  }
}

export type SyncPhase = "idle" | "syncing" | "up-to-date" | "offline" | "error";

export interface SyncStatus {
  phase: SyncPhase;
  pending: number;
  lastSyncedAt: number | null;
  message: string | null;
}

export const initialSyncStatus: SyncStatus = { phase: "idle", pending: 0, lastSyncedAt: null, message: null };

const PERIODIC_MS = 30_000;
const DEBOUNCE_MS = 300;
const MAX_BACKOFF_MS = 5 * 60_000;

/**
 * Pushes this device's outbox and pulls everyone else's operations. Safe to trigger often: runs
 * are serialized and debounced, and failures back off and retry.
 */
export class SyncEngine {
  private status: SyncStatus = initialSyncStatus;
  private running: Promise<void> | null = null;
  private again = false;
  private timer: ReturnType<typeof setTimeout> | undefined;
  private interval: ReturnType<typeof setInterval> | undefined;
  private failures = 0;
  private readonly onWake = () => this.schedule(0);
  private readonly onVisible = () => { if (typeof document === "undefined" || document.visibilityState === "visible") this.schedule(0); };

  constructor(
    private readonly store: LocalStore,
    private readonly api: SyncApi,
    private readonly deviceId: string,
    private readonly onStatus: (status: SyncStatus) => void,
    /** Called with operations pulled from the server and the Dataset after applying them. */
    private readonly onRemote: (operations: SyncOperation[], dataset: Dataset) => void | Promise<void> = () => undefined
  ) {}

  start(): void {
    this.interval = setInterval(() => this.schedule(0), PERIODIC_MS);
    if (typeof window !== "undefined") {
      window.addEventListener("online", this.onWake);
      document.addEventListener("visibilitychange", this.onVisible);
    }
    this.schedule(0);
  }

  stop(): void {
    clearInterval(this.interval);
    clearTimeout(this.timer);
    if (typeof window !== "undefined") {
      window.removeEventListener("online", this.onWake);
      document.removeEventListener("visibilitychange", this.onVisible);
    }
  }

  /** Requests a sync soon, e.g. after a local change. */
  schedule(delay = DEBOUNCE_MS): void {
    clearTimeout(this.timer);
    this.timer = setTimeout(() => { void this.sync(); }, delay);
  }

  /** Runs one full push and pull. Concurrent calls share the run and trigger one more after it. */
  sync(): Promise<void> {
    if (this.running) {
      this.again = true;
      return this.running;
    }
    this.running = this.run().finally(() => {
      this.running = null;
      if (this.again) {
        this.again = false;
        this.schedule(0);
      }
    });
    return this.running;
  }

  private async run(): Promise<void> {
    this.update({ phase: "syncing", message: null, pending: await this.store.getPendingOperationCount() });
    try {
      for (;;) {
        const batch = await this.store.getOutbox(MAX_PUSH_OPERATIONS);
        if (!batch.length) break;
        const response = await this.api.push({ deviceId: this.deviceId, operations: batch });
        if (!response.accepted.length) throw new SyncRequestError("The server did not accept any changes", null, true);
        await this.store.acknowledgeOperations(response.accepted);
      }
      let cursor = await this.store.getCursor();
      for (;;) {
        const page = await this.api.pull(cursor);
        if (page.operations.length) {
          const dataset = await this.store.applyRemote(page.operations);
          await this.onRemote(page.operations, dataset);
        }
        cursor = page.cursor;
        await this.store.setCursor(cursor);
        if (!page.hasMore) break;
      }
      this.failures = 0;
      this.update({ phase: "up-to-date", pending: await this.store.getPendingOperationCount(), lastSyncedAt: Date.now(), message: null });
    } catch (error) {
      const retryable = !(error instanceof SyncRequestError) || error.retryable;
      const offline = typeof navigator !== "undefined" && !navigator.onLine;
      this.update({
        phase: offline || (retryable && error instanceof SyncRequestError && error.status === null) ? "offline" : "error",
        pending: await this.store.getPendingOperationCount(),
        message: error instanceof Error ? error.message : "Sync failed"
      });
      if (retryable) {
        this.failures += 1;
        this.schedule(Math.min(MAX_BACKOFF_MS, 2_000 * 2 ** Math.min(this.failures, 8)));
      }
    }
  }

  private update(patch: Partial<SyncStatus>): void {
    this.status = { ...this.status, ...patch };
    this.onStatus(this.status);
  }
}
